import React, { FC, MouseEvent } from 'react';
import { useSelector } from 'react-redux';
import { Banner, IconAndTextButton } from '@blueprint/ui';
import { availableFromSelector, Filters } from '../../reducers/filters';
import {
    SearchContextType,
    Favourites,
    AnalyticsTaxonomy,
    AnalyticsEcommerce,
    SearchCategory,
    SearchSource
} from '../SearchContext';
import { ErrorArea } from '../banners/ErrorArea';
import { Listing } from './Listing';
import { IntegratedListingAdSlot } from '../helpers';
import { Listing as ListingType } from './Listings.types';
import { ListingsContainer, ResetButtonContainer } from './Listings.styles';
import { ApolloError } from '@apollo/client';
import {
    ecommerceClickTracking,
    listingCardTracking,
    imageTracking,
    getEcommerceDataForListing
} from './Listings.tracking';
import {
    segmentSearchResultClickEvent,
    segmentSearchResultImpressionEvent
} from 'search/helpers/segment';

type Props = {
    searchType: SearchContextType['searchType'];
    analyticsTaxonomyIdentifier: AnalyticsTaxonomy['searchIdentifier'];
    analyticsTaxonomy?: AnalyticsTaxonomy;
    searchCategory: SearchCategory;
    filters: Filters;
    currentSearchSource: SearchSource;
    error?: ApolloError;
    regularListings: ListingType[];
    extendedListings: ListingType[];
    onResetFilters: (evt: MouseEvent<HTMLButtonElement>) => void;
    favouritesListings: Favourites['listings'];
    authenticateSaveToggle: (listingId: string) => Promise<boolean>;
    authModal: React.ReactElement;
    analyticsEcommerce?: AnalyticsEcommerce;
    pageNumber: number;
};

const onCardClickCommon = (
    listingId: string,
    searchCategory: SearchCategory,
    filters: Filters,
    currentSearchSource: SearchSource,
    price: string,
    pageNumber: number,
    analyticsEcommerce?: AnalyticsEcommerce,
    analyticsTaxonomy?: AnalyticsTaxonomy
) => {
    const ecommerceData =
        analyticsEcommerce &&
        getEcommerceDataForListing(analyticsEcommerce, listingId);

    if (ecommerceData) {
        ecommerceClickTracking(ecommerceData);
    }

    if (analyticsTaxonomy) {
        segmentSearchResultClickEvent(
            analyticsTaxonomy,
            currentSearchSource,
            searchCategory,
            filters,
            listingId,
            price,
            pageNumber
        );
    }
};

const handleCardClick = (
    listingId: string,
    searchCategory: SearchCategory,
    filters: Filters,
    currentSearchSource: SearchSource,
    price: string,
    pageNumber: number,
    analyticsEcommerce?: AnalyticsEcommerce,
    analyticsTaxonomy?: AnalyticsTaxonomy
): void => {
    onCardClickCommon(
        listingId,
        searchCategory,
        filters,
        currentSearchSource,
        price,
        pageNumber,
        analyticsEcommerce,
        analyticsTaxonomy
    );
    listingCardTracking();
};

const handleImageClick = (
    listingId: string,
    searchCategory: SearchCategory,
    filters: Filters,
    currentSearchSource: SearchSource,
    price: string,
    pageNumber: number,
    analyticsEcommerce?: AnalyticsEcommerce,
    analyticsTaxonomy?: AnalyticsTaxonomy
): void => {
    onCardClickCommon(
        listingId,
        searchCategory,
        filters,
        currentSearchSource,
        price,
        pageNumber,
        analyticsEcommerce,
        analyticsTaxonomy
    );
    imageTracking();
};

const bannerStrapline = 'No results found';
const bannerTitle =
    'No results found. We were unable to locate any results matching your search criteria.';
const bannerText = 'Please amend your search options or try searching again.';

const bannerExtendedNoResultsStrapline = 'No results found';
const bannerExtendedNoResultsTitle =
    'We haven’t found any exact matches for your search criteria.';
const bannerExtendedNoResultsText =
    'However, we did find some properties with an expanded search radius';

const bannerExtendedResultsStrapline = 'No more exact results found';
const bannerExtendedResultsTitle =
    'There are no more exact matches for your search criteria.';
const bannerExtendedResultsText =
    'However, we also found these nearby properties that you may be interested in.';

export const Listings: FC<Props> = ({
    searchType,
    analyticsTaxonomyIdentifier,
    analyticsTaxonomy,
    analyticsEcommerce,
    searchCategory,
    filters,
    currentSearchSource,
    error,
    regularListings,
    extendedListings,
    onResetFilters,
    favouritesListings,
    authenticateSaveToggle,
    authModal,
    pageNumber
}) => {
    const { selectedValue: availableFromSelectedValue } = useSelector(
        availableFromSelector
    );

    if (error && error?.message === 'AREA_NOT_FOUND') {
        return (
            <ErrorArea
                areaName={analyticsTaxonomy?.areaName}
                searchType={searchType}
            />
        );
    }

    if (error) {
        return (
            <Banner
                strapline={bannerStrapline}
                title={bannerTitle}
                text={bannerText}
                data-testid="generic-error-banner"
                isMainHeadingOnPage={false}
            />
        );
    }

    const overrideViewsWithAvailability =
        searchType === 'to-rent' && !!availableFromSelectedValue;

    return (
        <>
            {authModal}

            {regularListings.length === 0 && extendedListings.length === 0 ? (
                <Banner
                    strapline={bannerStrapline}
                    title={bannerTitle}
                    text={bannerText}
                    data-testid="no-results-banner"
                    isMainHeadingOnPage={false}
                >
                    <ResetButtonContainer>
                        <IconAndTextButton
                            content="Reset filters"
                            iconId="reset"
                            iconIdHover="reset"
                            iconColor="white"
                            buttonColor="white"
                            backgroundColor="primary-brand"
                            hoverBackgroundColor="primary-light"
                            hasBorder
                            testId="reset_button"
                            hoverColor="white"
                            onClick={onResetFilters}
                        />
                    </ResetButtonContainer>
                </Banner>
            ) : (
                ''
            )}

            <ListingsContainer>
                {regularListings.map((listing, index) => (
                    <Listing
                        key={`listing_${listing.listingId}`}
                        dataTestId="search-result"
                        listingId={listing.listingId}
                        index={index}
                        flag={listing.flag}
                        underOffer={listing.underOffer}
                        favouritesListings={favouritesListings}
                        saveListing={authenticateSaveToggle}
                        onImageClick={() =>
                            handleImageClick(
                                listing.listingId,
                                searchCategory,
                                filters,
                                currentSearchSource,
                                listing.price,
                                pageNumber,
                                analyticsEcommerce,
                                analyticsTaxonomy
                            )
                        }
                        onCardClick={() =>
                            handleCardClick(
                                listing.listingId,
                                searchCategory,
                                filters,
                                currentSearchSource,
                                listing.price,
                                pageNumber,
                                analyticsEcommerce,
                                analyticsTaxonomy
                            )
                        }
                        onListingView={() => {
                            if (!analyticsTaxonomy) return;

                            segmentSearchResultImpressionEvent(
                                analyticsTaxonomy,
                                currentSearchSource,
                                searchCategory,
                                filters,
                                listing.listingId,
                                listing.price,
                                pageNumber
                            );
                        }}
                        availableFrom={listing.availableFrom}
                        availableFromLabel={listing.availableFromLabel}
                        analyticsTaxonomy={analyticsTaxonomy}
                        searchType={searchType}
                        searchIdentifier={analyticsTaxonomyIdentifier}
                        priceDrop={listing.priceDrop}
                        overrideViewsWithAvailability={
                            overrideViewsWithAvailability
                        }
                        address={listing.address}
                        branch={listing.branch}
                        features={listing.features}
                        highlights={listing.highlights}
                        image={listing.image}
                        isPremium={listing.isPremium}
                        listingUris={listing.listingUris}
                        numberOfFloorPlans={listing.numberOfFloorPlans}
                        numberOfImages={listing.numberOfImages}
                        numberOfVideos={listing.numberOfVideos}
                        numberOfViews={listing.numberOfViews}
                        otherPropertyImages={listing.otherPropertyImages}
                        price={listing.price}
                        priceTitle={listing.priceTitle}
                        publishedOn={listing.publishedOn}
                        publishedOnLabel={listing.publishedOnLabel}
                        tags={listing.tags}
                        title={listing.title}
                        transports={listing.transports}
                        adSlot={
                            <>
                                <IntegratedListingAdSlot
                                    useMobileConfig={false}
                                    listingIndex={index}
                                />
                                <IntegratedListingAdSlot
                                    useMobileConfig={true}
                                    listingIndex={index}
                                />
                            </>
                        }
                    />
                ))}

                {regularListings.length === 0 && extendedListings.length > 0 ? (
                    <Banner
                        data-testid="extended-results-banner"
                        strapline={bannerExtendedNoResultsStrapline}
                        title={bannerExtendedNoResultsTitle}
                        text={bannerExtendedNoResultsText}
                        isMainHeadingOnPage={false}
                    />
                ) : (
                    ''
                )}

                {regularListings.length > 0 && extendedListings.length > 0 ? (
                    <Banner
                        data-testid="extended-results-banner"
                        strapline={bannerExtendedResultsStrapline}
                        title={bannerExtendedResultsTitle}
                        text={bannerExtendedResultsText}
                        isMainHeadingOnPage={false}
                    />
                ) : (
                    ''
                )}

                {extendedListings.map((listing, index) => (
                    <Listing
                        key={`listing_${listing.listingId}`}
                        dataTestId="extended-search-result"
                        listingId={listing.listingId}
                        index={index}
                        flag={listing.flag}
                        underOffer={listing.underOffer}
                        favouritesListings={favouritesListings}
                        saveListing={authenticateSaveToggle}
                        onImageClick={() =>
                            handleImageClick(
                                listing.listingId,
                                searchCategory,
                                filters,
                                currentSearchSource,
                                listing.price,
                                pageNumber,
                                analyticsEcommerce,
                                analyticsTaxonomy
                            )
                        }
                        onCardClick={() =>
                            handleCardClick(
                                listing.listingId,
                                searchCategory,
                                filters,
                                currentSearchSource,
                                listing.price,
                                pageNumber,
                                analyticsEcommerce,
                                analyticsTaxonomy
                            )
                        }
                        onListingView={() => {
                            if (!analyticsTaxonomy) return;

                            segmentSearchResultImpressionEvent(
                                analyticsTaxonomy,
                                currentSearchSource,
                                searchCategory,
                                filters,
                                listing.listingId,
                                listing.price,
                                pageNumber
                            );
                        }}
                        analyticsTaxonomy={analyticsTaxonomy}
                        availableFrom={listing.availableFrom}
                        availableFromLabel={listing.availableFromLabel}
                        searchType={searchType}
                        searchIdentifier={analyticsTaxonomyIdentifier}
                        priceDrop={listing.priceDrop}
                        overrideViewsWithAvailability={
                            overrideViewsWithAvailability
                        }
                        address={listing.address}
                        branch={listing.branch}
                        features={listing.features}
                        highlights={listing.highlights}
                        image={listing.image}
                        isPremium={listing.isPremium}
                        listingUris={listing.listingUris}
                        numberOfFloorPlans={listing.numberOfFloorPlans}
                        numberOfImages={listing.numberOfImages}
                        numberOfVideos={listing.numberOfVideos}
                        numberOfViews={listing.numberOfViews}
                        otherPropertyImages={listing.otherPropertyImages}
                        price={listing.price}
                        priceTitle={listing.priceTitle}
                        publishedOn={listing.publishedOn}
                        publishedOnLabel={listing.publishedOnLabel}
                        tags={listing.tags}
                        title={listing.title}
                        transports={listing.transports}
                    />
                ))}
            </ListingsContainer>
        </>
    );
};
